import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { SERVICE_DETAILS, SERVICE_ICONS } from "@/data/static";
import { SEO_SERVICES } from "@/data/seoServices";
import ServiceLanding from "@/pages/ServiceLanding";
import { LeadForm } from "@/components/common/LeadForm";
import { CheckCircle2, ArrowRight, Building2 } from "lucide-react";
import { BeforeAfterSlider } from "@/components/common/BeforeAfterSlider";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const slugToKey = {
  "facility-management": "fm",
  cleaning: "cleaning",
  maintenance: "maintenance",
  manpower: "manpower",
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();

  // SEO-optimized landing pages (17 services) take priority
  if (SEO_SERVICES[slug]) return <ServiceLanding />;

  const key = slugToKey[slug];
  const detail = SERVICE_DETAILS[slug];

  if (!key || !detail) return <Navigate to="/services" replace />;
  const Icon = SERVICE_ICONS[slug] || Building2;

  const benefits = lang === "ar" ? detail.benefits_ar : detail.benefits_en;
  const useCases = lang === "ar" ? detail.useCases_ar : detail.useCases_en;
  const faqs = lang === "ar" ? detail.faqs_ar : detail.faqs_en;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: t(`services.${key}.title`),
    provider: { "@type": "LocalBusiness", name: COMPANY.brandFull },
    areaServed: ["Dubai", "Abu Dhabi", "Sharjah", "Ajman"],
    description: t(`services.${key}.long`),
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid={`service-detail-${slug}`}>
      <SEO
        title={`${t(`services.${key}.title`)} in UAE | ${COMPANY.brand}`}
        description={t(`services.${key}.long`)}
        jsonLd={jsonLd}
      />
      <SEO jsonLd={faqLd} jsonLdId={`faq-${slug}-jsonld`} />

      {/* Hero */}
      <section className="relative bg-white pt-16 lg:pt-24">
        <div className="container-orion">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Link to="/services" className="text-sm text-slate-500 hover:text-blue-700">
                ← {t("nav.services")}
              </Link>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="text-eyebrow">{t("services.sectionLabel")}</div>
              </div>
              <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.02] text-slate-900">
                {t(`services.${key}.title`)}
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                {t(`services.${key}.long`)}
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-slate-100 border border-slate-200">
                <img
                  src={detail.image}
                  alt={t(`services.${key}.title`)}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits + Use Cases */}
      <section className="py-24 lg:py-28 bg-slate-50 mt-20">
        <div className="container-orion">
          {slug === "cleaning" && (
            <div className="mb-16">
              <div className="max-w-3xl mb-8">
                <div className="text-eyebrow text-blue-700">See the difference</div>
                <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 leading-tight">
                  Drag the slider. Before → After.
                </h2>
              </div>
              <BeforeAfterSlider
                beforeSrc="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                afterSrc="https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                beforeLabel="Before"
                afterLabel="After"
                aspectClass="aspect-[16/9]"
              />
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
            <div className="bg-white p-10">
              <div className="text-eyebrow text-blue-700">{t("serviceDetail.benefitsLabel")}</div>
              <ul className="mt-6 space-y-4">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                    <span className="text-slate-700 leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-10">
              <div className="text-eyebrow text-blue-700">{t("serviceDetail.useCasesLabel")}</div>
              <ul className="mt-6 space-y-4">
                {useCases.map((u, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-blue-700 mt-0.5 shrink-0 rtl-flip" />
                    <span className="text-slate-700 leading-relaxed">{u}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-28 bg-white">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-eyebrow">{t("serviceDetail.faqLabel")}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 leading-tight">
              {t("serviceDetail.faqLabel")}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              {t("contact.desc")}
            </p>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="text-left font-heading text-lg font-medium text-slate-900 hover:text-blue-700"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA + Lead form */}
      <section className="py-24 lg:py-28 bg-slate-50">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="text-eyebrow text-blue-700">{t("serviceDetail.ctaTitle")}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              {t("ctaBanner.title")}
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">{t("serviceDetail.ctaDesc")}</p>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xl shadow-blue-900/5">
              <LeadForm pageSource={`service-${slug}`} defaultService={slug} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
